import { useState } from 'react';
import { X } from 'lucide-react';
import { t } from '@/i18n';
import { useDialog } from '@/hooks/useDialog';
import { usePreferences } from '@/hooks/usePreferences';
import { progressRepository } from '@/storage/repository';
import { Button } from './ui';
import './SettingsSheet.css';

interface SettingsSheetProps {
  onClose: () => void;
  /** Called after progress is erased, so the page can refresh its numbers. */
  onReset?: () => void;
}

/**
 * The few settings that exist, in a sheet rather than a navigation tab.
 *
 * There is no settings *tab* on purpose: four destinations is the whole
 * navigation, and two toggles plus a destructive action do not earn a fifth.
 */
export function SettingsSheet({ onClose, onReset }: SettingsSheetProps) {
  const { prefs, update } = usePreferences();
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  // Mounted only while open, so the confirm step always starts fresh.
  const panelRef = useDialog(onClose);

  const reset = async () => {
    await progressRepository.clear();
    setResetDone(true);
    setConfirmingReset(false);
    onReset?.();
  };

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label={t('settings.title')}>
      <div className="sheet__scrim" onClick={onClose} aria-hidden="true" />
      <div className="sheet__panel" ref={panelRef}>
        <div className="sheet__head">
          <h2 className="sheet__title">{t('settings.title')}</h2>
          <button
            type="button"
            className="sheet__close"
            onClick={onClose}
            aria-label={t('setup.close')}
          >
            <X size={22} aria-hidden="true" />
          </button>
        </div>

        <label className="sheet__row">
          <span className="sheet__row-text">
            <span className="sheet__row-title">{t('settings.sound')}</span>
          </span>
          <input
            type="checkbox"
            className="sheet__switch"
            checked={prefs.clickEnabled}
            onChange={(event) => update({ clickEnabled: event.target.checked })}
          />
        </label>

        <label className="sheet__row">
          <span className="sheet__row-text">
            <span className="sheet__row-title">{t('settings.adaptive')}</span>
            <span className="sheet__row-hint">{t('settings.adaptiveHint')}</span>
          </span>
          <input
            type="checkbox"
            className="sheet__switch"
            checked={prefs.adaptiveTempo}
            onChange={(event) => update({ adaptiveTempo: event.target.checked })}
          />
        </label>

        <div className="sheet__danger">
          {resetDone ? (
            <p className="sheet__done">{t('settings.resetDone')}</p>
          ) : confirmingReset ? (
            <>
              <p className="sheet__row-hint">{t('settings.resetHint')}</p>
              <div className="sheet__confirm">
                <Button variant="secondary" onClick={() => setConfirmingReset(false)}>
                  {t('settings.cancel')}
                </Button>
                <button type="button" className="sheet__destructive" onClick={() => void reset()}>
                  {t('settings.resetConfirm')}
                </button>
              </div>
            </>
          ) : (
            // Two deliberate steps: erasing practice history cannot be undone,
            // and it must not be one stray tap away.
            <button type="button" className="sheet__reset" onClick={() => setConfirmingReset(true)}>
              {t('settings.reset')}
            </button>
          )}
        </div>

        <p className="sheet__privacy">{t('settings.privacy')}</p>
      </div>
    </div>
  );
}
